/**
 * "Guides and tutorials" links from tool pages to the blog posts that teach
 * them (rendered above the footer by src/components/layout/RelatedGuides.tsx).
 *
 * Why: the tool pages hold almost all of the site's search authority, and
 * before this no tool page linked to a single tutorial, so blog posts were
 * reachable only from /blog. A post that targets what a tool page already
 * ranks for also loses to that page; the fix is a link from the tool page to
 * the tutorial, not another post for the same keyword.
 *
 * Titles and images are copied from WordPress (2026-10-07). When you publish
 * a post that teaches a tool, add it to BLOG_GUIDES and to that tool's route
 * below (most relevant first, at most four per route).
 */

export type BlogGuide = { title: string; image: string };

export const BLOG_GUIDES: Record<string, BlogGuide> = {
  'how-to-make-an-ai-short-film': {
    title: 'How to Make an AI Short Film with Seedance 2.5',
    image:
      'https://blog.easy-peasy.space/wp-content/uploads/2026/10/how-to-make-an-ai-short-film-featured.jpg',
  },
  'how-to-make-vox-style-videos-with-ai': {
    title: 'How to Make Vox-Style Videos with AI (Step-by-Step Guide)',
    image:
      'https://blog.easy-peasy.space/wp-content/uploads/2026/07/vox-style-videos-featured-v2.jpg',
  },
  'how-to-make-explainer-videos-with-ai': {
    title: 'How to Make Explainer Videos with AI (7 Animated Styles)',
    image:
      'https://blog.easy-peasy.space/wp-content/uploads/2026/10/how-to-make-explainer-videos-with-ai-featured.jpg',
  },
  'how-to-make-a-documentary-with-ai': {
    title: 'How to Make a Documentary with AI: Step-by-Step for Beginners',
    image:
      'https://blog.easy-peasy.space/wp-content/uploads/2026/08/documentary-ai-featured-scaled.jpg',
  },
  'realistic-ai-videos-seedance-2-0': {
    title: 'How to Create Authentic, Realistic AI Videos with Seedance 2.0',
    image:
      'https://blog.easy-peasy.space/wp-content/uploads/2026/07/seedance-realistic-video-featured.jpeg',
  },
  'how-to-create-viral-pet-videos-with-ai': {
    title: 'AI Pet Videos: How to Make Viral Cat and Dog Videos',
    image:
      'https://blog.easy-peasy.space/wp-content/uploads/2026/04/viral-pet-video-featured-scaled.jpeg',
  },
  'how-to-make-real-estate-videos-with-ai': {
    title:
      'How to Make Real Estate Videos with AI (Photos to Video Tour in Minutes)',
    image:
      'https://blog.easy-peasy.space/wp-content/uploads/2026/07/featured.jpg',
  },
  'talking-head-video-guide': {
    title: 'Talking Head Videos: What They Are, Examples and How to Make One',
    image:
      'https://blog.easy-peasy.space/wp-content/uploads/2026/02/95989bb1-5141-4f41-beb6-d20bd808a06b-scaled.png',
  },
  'ai-real-estate-reels': {
    title:
      'How Real Estate Agents Make AI Condo Reels with One Consistent Face and Voice',
    image:
      'https://blog.easy-peasy.space/wp-content/uploads/2026/08/realtor-condo-video-featured-scaled.jpg',
  },
  'how-to-animate-photos-with-ai': {
    title: 'How to Animate a Picture with AI: Step-by-Step Guide',
    image:
      'https://blog.easy-peasy.space/wp-content/uploads/2026/10/how-to-animate-a-picture-with-ai-featured.jpg',
  },
  'consistent-characters-ai-image-editing-with-flux-kontext': {
    title: 'Consistent Character AI: Keep the Same Character in Every Scene',
    image:
      'https://blog.easy-peasy.space/wp-content/uploads/2026/10/consistent-character-ai-featured.jpg',
  },
  'how-to-create-a-storyboard-with-ai-from-idea-to-visual-script-in-minutes': {
    title: 'How to Make a Storyboard with AI (Step-by-Step, With Examples)',
    image:
      'https://blog.easy-peasy.space/wp-content/uploads/2026/03/edited-image-6.jpeg',
  },
  'ai-baseball-fan-cam-trend': {
    title:
      'How to Make the Viral AI Stadium Fan Cam (NFL, NBA, KBO, F1 + 5 More)',
    image:
      'https://blog.easy-peasy.space/wp-content/uploads/2026/05/56d3daed-2cf1-49f0-b0c3-c6250a08300d.jpeg',
  },
  'how-to-add-anything-to-a-photo-with-ai': {
    title: 'How to Add Anything to a Photo with AI (50+ Ideas & Examples)',
    image:
      'https://blog.easy-peasy.space/wp-content/uploads/2026/07/add-to-photo-featured.jpg',
  },
  'how-to-edit-ai-images-with-visual-annotations-no-prompt-engineering-needed':
    {
      title:
        'How to Edit AI Images with Visual Annotations. No Prompt Engineering Needed',
      image:
        'https://blog.easy-peasy.space/wp-content/uploads/2026/02/Screenshot-2026-02-10-at-9.58.24-PM.png',
    },
  'mastering-ai-art-prompts-an-in-depth-guide-to-effective-and-creative-prompting':
    {
      title: 'AI Image Prompts: 50+ Copy-Paste Examples and a Simple Formula',
      image:
        'https://blog.easy-peasy.space/wp-content/uploads/2024/02/b2093191-1027-4f8e-97dc-39fde7a7e061-scaled.png',
    },
  'nano-banana-2-google-ai-image-model': {
    title: 'Nano Banana 2: Google’s Fastest AI Image Model Is Here',
    image:
      'https://blog.easy-peasy.space/wp-content/uploads/2026/03/909559ef-f6f8-4cb1-8167-560caea71531-scaled.png',
  },
  'how-to-create-ai-caricatures-from-a-single-photo': {
    title: 'How to Create AI Caricatures from a Single Photo',
    image:
      'https://blog.easy-peasy.space/wp-content/uploads/2024/12/Screenshot-2024-12-15-at-13.16.54.png',
  },
  'how-to-create-ai-generated-coloring-pages': {
    title: 'How to Create AI-Generated Coloring Pages',
    image:
      'https://blog.easy-peasy.space/wp-content/uploads/2024/11/Screenshot-2024-11-29-at-12.54.04.png',
  },
  'creating-magic-a-guide-to-generating-ai-art-in-disney-pixar-style': {
    title: 'A Guide to Generating AI Art in Disney/Pixar Style',
    image:
      'https://blog.easy-peasy.space/wp-content/uploads/2024/02/0af42e88-b986-4da4-9157-6df211aeacd5.png',
  },
  'unleash-your-nightmares-8-horrifying-art-styles-brought-to-life-with-easy-peasy-ai-image-generator':
    {
      title:
        'Unleash Your Nightmares: 8 Horrifying Art Styles Brought to Life with Easy Peasy AI Image Generator',
      image:
        'https://blog.easy-peasy.space/wp-content/uploads/2023/11/f589dd12-5fc5-4aec-b6e9-6d2d9b5537af.png',
    },
  'how-to-generate-sprite-sheets-with-ai-free-guide-for-game-developers': {
    title:
      'How to Generate Sprite Sheets with AI (Free Guide for Game Developers)',
    image:
      'https://blog.easy-peasy.space/wp-content/uploads/2026/02/sprite-generator-featured-scaled.jpeg',
  },
  'how-to-create-viral-ai-selfies-with-celebrities-on-movie-sets-full-workflow-prompts':
    {
      title:
        'How to Create Viral AI Selfies with Celebrities on Movie Sets (Full Workflow + Prompts)',
      image:
        'https://blog.easy-peasy.space/wp-content/uploads/2025/12/5573bc0b-c505-4563-901a-c6035703370b-scaled.png',
    },
  'sketchup-interior-design-ai-render': {
    title:
      'How to Turn SketchUp 3D Models into Photorealistic Interior Renders with AI',
    image:
      'https://blog.easy-peasy.space/wp-content/uploads/2026/03/sketchup-interior-design-ai-featured-scaled.jpeg',
  },
  'how-to-convert-2d-floor-plans-to-3d-isometric-maquettes-and-create-animated-video-tours':
    {
      title:
        'How to Convert 2D Floor Plans to 3D Isometric Maquettes and Create Animated Video Tours',
      image:
        'https://blog.easy-peasy.space/wp-content/uploads/2026/01/26c16a04-cbd1-454f-ab8d-0e244a09f4a4.jpeg',
    },
  'ai-for-real-estate-agents': {
    title: 'AI for Real Estate Agents: 12 Ways to Win More Listings',
    image:
      'https://blog.easy-peasy.space/wp-content/uploads/2026/07/ai-for-realtors-featured-scaled.jpg',
  },
  'how-to-generate-text-to-speech-in-specific-accents': {
    title: 'How to Generate Text-to-Speech in Specific Accents',
    image:
      'https://blog.easy-peasy.space/wp-content/uploads/2025/07/image-2025-07-27T163732.174.webp',
  },
  'how-to-generate-professional-australian-accent-text-to-speech': {
    title: 'How to Generate Professional Australian Accent Text-to-Speech',
    image:
      'https://blog.easy-peasy.space/wp-content/uploads/2025/11/fb4cb5d0-8a3d-4328-8333-1b0e43703d0f-1-scaled.jpg',
  },
  'how-to-add-ai-chatbot-to-website': {
    title:
      'How to Add an AI Chatbot to Your Website in 2026 (No Code Required)',
    image:
      'https://blog.easy-peasy.space/wp-content/uploads/2026/03/chatbot-builder-featured-scaled.jpeg',
  },
  'ai-voice-agent-for-website': {
    title:
      'How to Add an AI Voice Agent to Your Website (No Phone Number Needed)',
    image:
      'https://blog.easy-peasy.space/wp-content/uploads/2026/09/ai-voice-agent-v2-featured.jpg',
  },
  'how-to-create-custom-ai-bot': {
    title:
      'How to Create a Custom AI Bot and Share It — Easy-Peasy.AI vs Custom GPTs vs Gemini Gems',
    image:
      'https://blog.easy-peasy.space/wp-content/uploads/2026/03/custom-ai-bot-featured-scaled.jpeg',
  },
  'meta-business-agent-vs-your-own-ai-agent': {
    title:
      'Meta Business Agent vs. Your Own AI Agent: Which Should Answer Your WhatsApp?',
    image:
      'https://blog.easy-peasy.space/wp-content/uploads/2026/09/meta-business-agent-featured-scaled.jpg',
  },
  'ai-agent-examples': {
    title: '10 AI Agent Examples: Real Tasks You Can Hand Off Today',
    image:
      'https://blog.easy-peasy.space/wp-content/uploads/2026/09/ai-agent-examples-featured.jpg',
  },
  'marky-ai-agent-guide': {
    title: 'Meet Marky: The AI Agent That Replaces ChatGPT, Claude, and Gemini',
    image:
      'https://blog.easy-peasy.space/wp-content/uploads/2026/02/marky-featured-scaled.jpeg',
  },
  'marky-agent-skills-guide': {
    title: 'How to Use Skills in Marky Agent: Teach Your AI New Tricks',
    image:
      'https://blog.easy-peasy.space/wp-content/uploads/2026/05/skills-marky-featured-scaled.jpeg',
  },
  'marky-browser-agent-chrome-extension': {
    title:
      'How to Let Marky Work in Your Own Browser with the Easy-Peasy.AI Browser Agent',
    image:
      'https://blog.easy-peasy.space/wp-content/uploads/2026/09/browser-agent-featured-scaled.jpg',
  },
  'how-to-build-a-website-with-ai': {
    title:
      'Best AI Website Builder in 2026: Custom Websites That Don’t Look Like Purple AI Slop',
    image:
      'https://blog.easy-peasy.space/wp-content/uploads/2026/03/ai-website-builder-featured-scaled.jpeg',
  },
  'why-is-my-website-not-showing-up-on-google': {
    title: 'Why Is My Website Not Showing Up on Google? 6 Reasons and Fixes',
    image:
      'https://blog.easy-peasy.space/wp-content/uploads/2026/11/why-is-my-website-not-showing-up-on-google-featured.jpg',
  },
  'ai-therapy-progress-notes': {
    title:
      'How to Write Therapy Progress Notes with AI (SOAP, DAP & BIRP Examples)',
    image:
      'https://blog.easy-peasy.space/wp-content/uploads/2026/07/ai-progress-notes-featured-scaled.jpg',
  },
  'best-ai-progress-note-generators': {
    title: '10 Best AI Progress Note Generators for Therapists (2026)',
    image:
      'https://blog.easy-peasy.space/wp-content/uploads/2026/06/best-ai-progress-note-generators-featured.jpeg',
  },
  'ai-social-stories-generator': {
    title:
      'How to Create Social Stories with AI – Free Generator for Autism & Special Needs',
    image:
      'https://blog.easy-peasy.space/wp-content/uploads/2026/03/social-stories-generator-featured-scaled.jpeg',
  },
  'how-to-make-a-carousel-for-instagram-linkedin-with-ai': {
    title: 'How to Make a Carousel for Instagram & LinkedIn with AI',
    image:
      'https://blog.easy-peasy.space/wp-content/uploads/2026/07/image-80.jpeg',
  },
  'how-to-add-ai-to-your-organization': {
    title: 'How to Add AI to Your Organization (Complete Guide)',
    image:
      'https://blog.easy-peasy.space/wp-content/uploads/2026/03/ai-organization-featured-scaled.jpeg',
  },
  'how-to-automate-tasks-with-ai-a-beginners-guide-2026': {
    title: 'How to Automate Tasks with AI: A Beginner’s Guide (2026)',
    image:
      'https://blog.easy-peasy.space/wp-content/uploads/2026/06/marky-ai-automation-featured.jpeg',
  },
  'ai-property-launch-video': {
    title:
      'How to Make a 30-Second Property Launch Video with AI (Before the Building Exists)',
    image:
      'https://blog.easy-peasy.space/wp-content/uploads/2026/08/property-launch-video-featured.jpg',
  },
};

export type RelatedGuideRoute = {
  /** Locale-free path without a trailing slash, e.g. "/ai-video-generator". */
  path: string;
  /** Also match every path below it ("/chatbots" covers "/chatbots/sales"). */
  prefix?: boolean;
  /** BLOG_GUIDES keys, most relevant first. */
  guides: string[];
};

const VIDEO_GUIDES = [
  'how-to-make-an-ai-short-film',
  'how-to-make-vox-style-videos-with-ai',
  'how-to-make-explainer-videos-with-ai',
  'how-to-make-a-documentary-with-ai',
];
// Teaches trimming, extending, Edit with AI and the 4K upscale.
const VIDEO_FINISHING_GUIDES = ['how-to-make-an-ai-short-film'];
const TALKING_VIDEO_GUIDES = [
  'talking-head-video-guide',
  'ai-real-estate-reels',
  'how-to-make-explainer-videos-with-ai',
];
const IMAGE_EDIT_GUIDES = [
  'how-to-add-anything-to-a-photo-with-ai',
  'how-to-edit-ai-images-with-visual-annotations-no-prompt-engineering-needed',
];
const PROMPT_GUIDE =
  'mastering-ai-art-prompts-an-in-depth-guide-to-effective-and-creative-prompting';
const THERAPY_NOTE_GUIDES = [
  'ai-therapy-progress-notes',
  'best-ai-progress-note-generators',
];
const CAROUSEL_GUIDES = [
  'how-to-make-a-carousel-for-instagram-linkedin-with-ai',
];

export const RELATED_GUIDE_ROUTES: RelatedGuideRoute[] = [
  // Video
  { path: '/ai-video-generator', guides: VIDEO_GUIDES },
  { path: '/ai-video-extender', guides: VIDEO_FINISHING_GUIDES },
  { path: '/ai-video-upscaler', guides: VIDEO_FINISHING_GUIDES },
  { path: '/edit-video-with-ai', guides: VIDEO_FINISHING_GUIDES },
  {
    path: '/ai-video-generator/seedance-2',
    guides: [
      'realistic-ai-videos-seedance-2-0',
      'how-to-create-viral-pet-videos-with-ai',
      'how-to-make-vox-style-videos-with-ai',
    ],
  },
  {
    path: '/ai-video-generator/seedance-2-5',
    guides: [
      'how-to-make-an-ai-short-film',
      'how-to-make-vox-style-videos-with-ai',
      'how-to-make-a-documentary-with-ai',
      'realistic-ai-videos-seedance-2-0',
    ],
  },
  {
    path: '/create-video',
    guides: [
      'how-to-make-a-documentary-with-ai',
      'how-to-create-a-storyboard-with-ai-from-idea-to-visual-script-in-minutes',
      'how-to-make-explainer-videos-with-ai',
      'how-to-make-vox-style-videos-with-ai',
    ],
  },
  { path: '/ai-video-generator/talking', guides: TALKING_VIDEO_GUIDES },
  { path: '/ai-video-generator/talking-head', guides: TALKING_VIDEO_GUIDES },
  { path: '/ai-video-generator/talking-avatar', guides: TALKING_VIDEO_GUIDES },
  { path: '/ai-video-generator/make-image-talk', guides: TALKING_VIDEO_GUIDES },
  { path: '/ai-video-generator/spokesperson', guides: TALKING_VIDEO_GUIDES },
  {
    path: '/ai-animate-image',
    guides: [
      'how-to-animate-photos-with-ai',
      'how-to-create-viral-pet-videos-with-ai',
      'ai-baseball-fan-cam-trend',
    ],
  },

  // Images
  {
    path: '/ai-image-generator',
    guides: [
      ...IMAGE_EDIT_GUIDES,
      PROMPT_GUIDE,
      'nano-banana-2-google-ai-image-model',
    ],
  },
  {
    path: '/ai-image-generator/nano-banana',
    guides: ['nano-banana-2-google-ai-image-model', ...IMAGE_EDIT_GUIDES],
  },
  ...['character', 'character-design'].map((style) => ({
    path: `/ai-image-generator/${style}`,
    guides: [
      'consistent-characters-ai-image-editing-with-flux-kontext',
      PROMPT_GUIDE,
    ],
  })),
  {
    path: '/ai-image-generator/caricature',
    guides: ['how-to-create-ai-caricatures-from-a-single-photo', PROMPT_GUIDE],
  },
  {
    path: '/ai-image-generator/coloring-page',
    guides: ['how-to-create-ai-generated-coloring-pages', PROMPT_GUIDE],
  },
  {
    path: '/ai-image-generator/coloring-book',
    guides: ['how-to-create-ai-generated-coloring-pages', PROMPT_GUIDE],
  },
  ...['pixar', 'disney', 'disney-pixar-poster'].map((style) => ({
    path: `/ai-image-generator/${style}`,
    guides: [
      'creating-magic-a-guide-to-generating-ai-art-in-disney-pixar-style',
      PROMPT_GUIDE,
    ],
  })),
  {
    path: '/ai-image-generator/horror-art',
    guides: [
      'unleash-your-nightmares-8-horrifying-art-styles-brought-to-life-with-easy-peasy-ai-image-generator',
      PROMPT_GUIDE,
    ],
  },
  ...['sprite-sheet', 'pixel-art'].map((style) => ({
    path: `/ai-image-generator/${style}`,
    guides: [
      'how-to-generate-sprite-sheets-with-ai-free-guide-for-game-developers',
    ],
  })),
  ...['celebrity', 'selfie'].map((style) => ({
    path: `/ai-image-generator/${style}`,
    guides: [
      'how-to-create-viral-ai-selfies-with-celebrities-on-movie-sets-full-workflow-prompts',
      'how-to-add-anything-to-a-photo-with-ai',
    ],
  })),
  {
    path: '/ai-interior-design-generator',
    prefix: true,
    guides: [
      'sketchup-interior-design-ai-render',
      'how-to-convert-2d-floor-plans-to-3d-isometric-maquettes-and-create-animated-video-tours',
      'ai-for-real-estate-agents',
    ],
  },

  // Audio
  {
    path: '/text-to-speech',
    prefix: true,
    guides: [
      'how-to-generate-text-to-speech-in-specific-accents',
      'how-to-generate-professional-australian-accent-text-to-speech',
    ],
  },
  {
    path: '/text-to-speech/australian',
    guides: [
      'how-to-generate-professional-australian-accent-text-to-speech',
      'how-to-generate-text-to-speech-in-specific-accents',
    ],
  },

  // Agents
  {
    path: '/chatbots',
    prefix: true,
    guides: [
      'how-to-add-ai-chatbot-to-website',
      'ai-voice-agent-for-website',
      'meta-business-agent-vs-your-own-ai-agent',
      'how-to-create-custom-ai-bot',
    ],
  },

  // Marky
  {
    path: '/marky',
    guides: [
      'ai-agent-examples',
      'marky-ai-agent-guide',
      'marky-agent-skills-guide',
      'marky-browser-agent-chrome-extension',
    ],
  },
  {
    path: '/marky/website-builder',
    prefix: true,
    guides: [
      'how-to-build-a-website-with-ai',
      'why-is-my-website-not-showing-up-on-google',
      'ai-agent-examples',
    ],
  },
  {
    path: '/ai-workflows',
    guides: ['how-to-automate-tasks-with-ai-a-beginners-guide-2026'],
  },
  { path: '/b2b', guides: ['how-to-add-ai-to-your-organization'] },

  // Templates
  ...[
    'progress-note-generator',
    'soap-note-generator',
    'dap-note-generator',
    'birp-note-generator',
  ].map((slug) => ({
    path: `/templates/${slug}`,
    guides: THERAPY_NOTE_GUIDES,
  })),
  {
    path: '/templates/social-stories-generator',
    guides: ['ai-social-stories-generator'],
  },
  {
    path: '/templates/linkedin-carousel-generator',
    guides: CAROUSEL_GUIDES,
  },
  {
    path: '/templates/instagram-carousel-generator',
    guides: CAROUSEL_GUIDES,
  },
];

export type RelatedGuide = BlogGuide & { slug: string };

/**
 * Guides for a locale-free path: an exact route wins, otherwise the longest
 * matching prefix route. Unknown slugs are skipped.
 */
export function getRelatedGuides(path: string): RelatedGuide[] {
  const clean = path.split(/[?#]/)[0].replace(/\/+$/, '') || '/';
  const exact = RELATED_GUIDE_ROUTES.find((route) => route.path === clean);
  const route =
    exact ||
    RELATED_GUIDE_ROUTES.filter(
      (candidate) => candidate.prefix && clean.startsWith(`${candidate.path}/`),
    ).sort((a, b) => b.path.length - a.path.length)[0];
  if (!route) return [];
  return route.guides
    .filter((slug) => BLOG_GUIDES[slug])
    .slice(0, 4)
    .map((slug) => ({ slug, ...BLOG_GUIDES[slug] }));
}
